import { For, Show, Suspense, createSignal } from "solid-js";
import { useParams, A, createAsync, useAction, type RouteDefinition } from "@solidjs/router";
import { Title } from "@solidjs/meta";
import { AppShell } from "~/components/layout";
import {
  Button, Badge, Card, CardContent, Skeleton, EmptyState, ScrollArea,
  Tabs, TabsList, TabsTrigger, TabsContent, Breadcrumbs
} from "~/components/ui";
import { getAnalysisRun, getVideo, rerunAnalysis, addToDossier } from "~/server/bff";
import { AddToDossierModal } from "~/components/modals";
import { formatRelativeTime, formatDuration } from "~/lib/utils";
import {
  ArrowLeft, Download, FolderPlus, RefreshCw, Check, X, Eye,
  Table, LayoutGrid, GitCompare, ExternalLink, Loader2
} from "lucide-solid";

export const route = {
  preload: ({ params }) => getAnalysisRun(params.id as string),
} satisfies RouteDefinition;

export default function AnalysisRunDetailPage() {
  const params = useParams();
  const [viewMode, setViewMode] = createSignal<"table" | "cards" | "matrix">("table");
  const [filterStatus, setFilterStatus] = createSignal<string | null>(null);
  const [isRerunning, setIsRerunning] = createSignal(false);
  const [showDossierModal, setShowDossierModal] = createSignal(false);
  const [actionLoading, setActionLoading] = createSignal<string | null>(null);

  const analysisRun = createAsync(() => getAnalysisRun(params.id || ""), { deferStream: true });

  const rerunAction = useAction(rerunAnalysis);

  const handleRerun = async () => {
    if (!params.id) return;
    setIsRerunning(true);
    try {
      const formData = new FormData();
      formData.append("runId", params.id);
      await rerunAction(formData);
    } catch (error) {
      console.error("Failed to rerun analysis:", error);
    } finally {
      setIsRerunning(false);
    }
  };

  return (
    <AppShell>
      <Title>{analysisRun()?.name ?? "Analysis"} - IntelAnalyst</Title>
      <div class="flex h-[calc(100vh-4rem)]">
        <div class="w-64 border-r flex flex-col bg-muted/30">
          <div class="p-4 border-b">
            {/* Breadcrumbs */}
            <Breadcrumbs
              items={[
                { label: "Investigate", href: "/investigate/analysis" },
                { label: "Analysis", href: "/investigate/analysis" },
                { label: analysisRun()?.name ?? "Loading..." },
              ]}
              showHome
              class="mb-3"
            />
            <Suspense fallback={<Skeleton class="h-6 w-full" />}>
              <Show when={analysisRun()}>
                {(run) => (
                  <>
                    <h1 class="font-semibold text-lg leading-tight">{run().name}</h1>
                    <div class="flex items-center gap-2 mt-2">
                      <Badge variant="outline" class="text-xs font-mono">
                        {run().mode}
                      </Badge>
                      <Badge
                        variant={run().status === "completed" ? "success" : "outline"}
                        class="text-xs"
                      >
                        {run().status}
                      </Badge>
                    </div>
                    <p class="text-xs text-muted-foreground mt-2">
                      {run().sourceVideoIds.length} source videos
                    </p>
                  </>
                )}
              </Show>
            </Suspense>
          </div>

          <ScrollArea class="flex-1 p-2">
            <p class="px-2 py-1 text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Sources
            </p>
            <Suspense fallback={<SourcesSkeleton />}>
              <Show when={analysisRun()}>
                {(run) => (
                  <div class="space-y-1">
                    <For each={run().sourceVideoIds}>
                      {(videoId) => <SourceItem videoId={videoId} />}
                    </For>
                  </div>
                )}
              </Show>
            </Suspense>
          </ScrollArea>

          <div class="p-2 border-t space-y-1">
            <Button 
              variant="outline" 
              size="sm" 
              class="w-full justify-start"
              onClick={() => void handleRerun()}
              disabled={isRerunning()}
            >
              <Show when={isRerunning()} fallback={<RefreshCw class="h-4 w-4 mr-2" />}>
                <Loader2 class="h-4 w-4 mr-2 animate-spin" />
              </Show>
              Re-run Analysis
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              class="w-full justify-start"
              onClick={() => {
                const run = analysisRun();
                if (!run) return;
                
                // Export as JSON
                const exportData = {
                  name: run.name,
                  mode: run.mode,
                  template: run.templateName,
                  status: run.status,
                  sourceVideoIds: run.sourceVideoIds,
                  results: run.results,
                  exportedAt: new Date().toISOString(),
                };
                
                const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: "application/json" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `${run.name.replace(/[^a-z0-9]/gi, "_")}_analysis.json`;
                a.click();
                URL.revokeObjectURL(url);
              }}
            >
              <Download class="h-4 w-4 mr-2" />
              Export Results
            </Button>
          </div>
        </div>

        <div class="flex-1 flex flex-col overflow-hidden">
          <div class="border-b px-6 py-3 flex items-center justify-between">
            <div class="flex items-center gap-4">
              <div class="flex rounded-md border p-0.5">
                <Button
                  variant={viewMode() === "table" ? "secondary" : "ghost"}
                  size="icon-sm"
                  onClick={() => setViewMode("table")}
                >
                  <Table class="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode() === "cards" ? "secondary" : "ghost"}
                  size="icon-sm"
                  onClick={() => setViewMode("cards")}
                >
                  <LayoutGrid class="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode() === "matrix" ? "secondary" : "ghost"}
                  size="icon-sm"
                  onClick={() => setViewMode("matrix")}
                >
                  <GitCompare class="h-4 w-4" />
                </Button>
              </div>

              <div class="flex gap-1">
                <Badge
                  variant={filterStatus() === null ? "default" : "outline"}
                  class="cursor-pointer"
                  onClick={() => setFilterStatus(null)}
                >
                  All
                </Badge>
                <Badge
                  variant={filterStatus() === "unreviewed" ? "default" : "outline"}
                  class="cursor-pointer"
                  onClick={() => setFilterStatus("unreviewed")}
                >
                  <Eye class="h-3 w-3 mr-1" />
                  Unreviewed
                </Badge>
                <Badge
                  variant={filterStatus() === "accepted" ? "success" : "outline"}
                  class="cursor-pointer"
                  onClick={() => setFilterStatus("accepted")}
                >
                  <Check class="h-3 w-3 mr-1" />
                  Accepted
                </Badge>
                <Badge
                  variant={filterStatus() === "rejected" ? "destructive" : "outline"}
                  class="cursor-pointer"
                  onClick={() => setFilterStatus("rejected")}
                >
                  <X class="h-3 w-3 mr-1" />
                  Rejected
                </Badge>
              </div>
            </div>

            <Button size="sm" onClick={() => setShowDossierModal(true)}>
              <FolderPlus class="h-4 w-4 mr-1" />
              Save to Dossier
            </Button>
          </div>

          <ScrollArea class="flex-1 p-6">
            <Show
              when={viewMode() === "table"}
              fallback={
                <Show
                  when={viewMode() === "cards"}
                  fallback={
                    <Card>
                      <CardContent class="p-8 text-center text-muted-foreground">
                        <GitCompare class="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <Badge variant="secondary" class="mb-2">Coming Soon</Badge>
                        <p class="font-medium">Matrix View</p>
                        <p class="text-sm">Compare results side by side across multiple sources</p>
                      </CardContent>
                    </Card>
                  }
                >
                  <div class="grid gap-4 md:grid-cols-2">
                    <For each={[1, 2, 3, 4]} fallback={<EmptyResults />}>
                      {() => (
                        <Card class="group">
                          <CardContent class="p-4">
                            <div class="flex items-start justify-between mb-3">
                              <Badge variant="outline" class="text-xs">unreviewed</Badge>
                              <div class="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <Button 
                                  variant="ghost" 
                                  size="icon-sm"
                                  onClick={() => console.log("Accept result")}
                                  title="Accept"
                                >
                                  <Check class="h-3 w-3 text-green-500" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="icon-sm"
                                  onClick={() => console.log("Reject result")}
                                  title="Reject"
                                >
                                  <X class="h-3 w-3 text-red-500" />
                                </Button>
                              </div>
                            </div>
                            <div class="space-y-2 text-sm">
                              <div>
                                <span class="text-muted-foreground">Event:</span>
                                <span class="ml-2">GPT-5 release prediction</span>
                              </div>
                              <div>
                                <span class="text-muted-foreground">Timeframe:</span>
                                <span class="ml-2">Late 2024</span>
                              </div>
                              <div>
                                <span class="text-muted-foreground">Confidence:</span>
                                <span class="ml-2">High</span>
                              </div>
                            </div>
                            <div class="mt-3 pt-3 border-t text-xs text-muted-foreground">
                              <a href="#" class="hover:text-foreground flex items-center gap-1">
                                <ExternalLink class="h-3 w-3" />
                                View evidence
                              </a>
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </For>
                  </div>
                </Show>
              }
            >
              <div class="rounded-lg border overflow-hidden">
                <table class="w-full text-sm">
                  <thead class="bg-muted/50">
                    <tr>
                      <th class="text-left p-3 font-medium">Status</th>
                      <th class="text-left p-3 font-medium">Source</th>
                      <th class="text-left p-3 font-medium">Event</th>
                      <th class="text-left p-3 font-medium">Timeframe</th>
                      <th class="text-left p-3 font-medium">Confidence</th>
                      <th class="text-left p-3 font-medium">Evidence</th>
                      <th class="p-3" />
                    </tr>
                  </thead>
                  <tbody>
                    <For each={[1, 2, 3, 4, 5]} fallback={<EmptyResults />}>
                      {() => (
                        <tr class="border-t hover:bg-muted/30">
                          <td class="p-3">
                            <Badge variant="outline" class="text-xs">unreviewed</Badge>
                          </td>
                          <td class="p-3">
                            <a href="#" class="text-primary hover:underline">AI Explained</a>
                          </td>
                          <td class="p-3">GPT-5 release prediction</td>
                          <td class="p-3">Late 2024</td>
                          <td class="p-3">High</td>
                          <td class="p-3">
                            <a href="#" class="text-primary hover:underline text-xs font-mono">
                              @7:36
                            </a>
                          </td>
                          <td class="p-3">
                            <div class="flex gap-1">
                              <Button 
                                variant="ghost" 
                                size="icon-sm"
                                onClick={() => {
                                  // TODO: Wire up when results have IDs
                                  console.log("Accept result");
                                }}
                                title="Accept this result"
                              >
                                <Check class="h-3 w-3 text-green-500" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon-sm"
                                onClick={() => {
                                  // TODO: Wire up when results have IDs
                                  console.log("Reject result");
                                }}
                                title="Reject this result"
                              >
                                <X class="h-3 w-3 text-red-500" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon-sm"
                                onClick={() => setShowDossierModal(true)}
                                title="Add to dossier"
                              >
                                <FolderPlus class="h-3 w-3" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </For>
                  </tbody>
                </table>
              </div>
            </Show>
          </ScrollArea>
        </div>
      </div>
      <AddToDossierModal
        isOpen={showDossierModal()}
        onClose={() => setShowDossierModal(false)}
        itemType="analysis"
        itemId={params.id || ""}
        itemTitle={analysisRun()?.name || "Analysis Results"}
        onAdd={(dossierId, sectionId) => {
          console.log(`Added to dossier ${dossierId}, section ${sectionId}`);
        }}
      />
    </AppShell>
  );
}

function SourceItem(props: { videoId: string }) {
  const video = createAsync(() => getVideo(props.videoId), { deferStream: true });

  return (
    <Suspense fallback={<Skeleton class="h-12 w-full" />}>
      <Show when={video()}>
        {(v) => (
          <A
            href={`/video/${v().id}`}
            class="flex items-center gap-2 p-2 rounded hover:bg-accent"
          >
            <img src={v().thumbnailUrl} alt="" class="h-8 w-14 rounded object-cover" />
            <div class="flex-1 min-w-0">
              <p class="text-xs font-medium line-clamp-1">{v().title}</p>
              <p class="text-xs text-muted-foreground">{v().channel?.name}</p>
            </div>
          </A>
        )}
      </Show>
    </Suspense>
  );
}

function SourcesSkeleton() {
  return (
    <div class="space-y-1">
      <For each={[1, 2, 3]}>
        {() => <Skeleton class="h-12 w-full" />}
      </For>
    </div>
  );
}

function EmptyResults() {
  return (
    <tr>
      <td colspan="7" class="p-8 text-center text-muted-foreground">
        No results yet. Run the analysis to extract data.
      </td>
    </tr>
  );
}

// Add AddToDossierModal at the end of the component before closing </AppShell>
